import {
  Button,
  Divider,
  FlexBox,
  Modal,
  ModalBody,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { useCallback, useState } from 'react';

import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import {
  FacebookAuthenticationData,
  FacebookLoginButton,
  FacebookLoginButtonProps,
} from '../../../../facebookLoginButton';

import { FacebookConnectModal } from './components/facebookConnectModal/facebookConnectModal';
import styles from './facebookAuthenticator.module.scss';

type FacebookAuthenticatorProps = PropsWithChildren & {
  /** The id of the facebook app. */
  appId: string;
  /** The props of the facebook login button. */
  facebookButtonProps?: Omit<
    FacebookLoginButtonProps,
    'scope' | 'onSuccess' | 'onError'
  >;
  /** The callback for when facebook was authenticated. */
  onSuccess: (queueId: string) => void;
};

/**
 * A component that handles authenticating to Facebook and setting up the
 * Facebook Messenger channel.
 */
export const FacebookAuthenticator = ({
  appId,
  facebookButtonProps,
  children,
  onSuccess,
}: FacebookAuthenticatorProps): JSX.Element => {
  const dispatch = useAppDispatch();

  const [prerequisitesModalOpen, setPrerequisitesModalOpen] = useState(false);

  /** Handles closing the modal. */
  const handleClose = useCallback(() => {
    setPrerequisitesModalOpen(false);
  }, []);

  /** Handles the facebook authentication. */
  const handleFacebookAuthenticated = async (
    facebookAuthenticationData: FacebookAuthenticationData,
  ) => {
    const { queueId } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:facebookConnect',
          arg: {
            appId,
            facebookAuthenticationData,
          },
        },
      }),
    );

    onSuccess(queueId);
    handleClose();
  };

  /** The authentication prerequisites. */
  const prerequisites: string[] = [
    'An existing created Facebook page.',
    'A Facebook user with owner or admin role on the target pages.',
  ];

  /**
   * This are tips that can help admins understand how the integration works and
   * help them not mess it up.
   */
  const tips: string[] = [
    'Every time a user connects Facebook pages, their previous granted authentication is invalidated. Bear this in mind when you have to create more than one page, as you should not add them one by one, but all at once.',
    'Changing the Facebook password or other important settings might cause the connection to be invalidated, control the connection state by checking that you still receive messages, and reconnect the page if necessary.',
  ];

  return (
    <>
      <Button
        onClick={() => setPrerequisitesModalOpen(true)}
        {...facebookButtonProps}
      >
        {children}
      </Button>

      <Modal
        isOpen={prerequisitesModalOpen}
        className={styles.modal}
        onClose={handleClose}
      >
        <ModalHead title="Facebook for Business" onClose={handleClose} />

        <ModalBody>
          <FlexBox gap={'300'} flexDirection="column">
            <Text.Paragraph>
              Following the connection of a page, all Facebook messages will be
              seamlessly routed to Dixa, while agent responses will also be
              displayed on Facebook.
            </Text.Paragraph>

            <Divider />

            <Text.Paragraph>{'What you need'}</Text.Paragraph>
            <ul>
              {prerequisites.map((text, index) => (
                <li key={index}>
                  <Text.Paragraph>{text}</Text.Paragraph>
                </li>
              ))}
            </ul>

            <Text.Paragraph>{'Also good to know'}</Text.Paragraph>
            <ul>
              {tips.map((text, index) => (
                <li key={index}>
                  <Text.Paragraph>{text}</Text.Paragraph>
                </li>
              ))}
            </ul>
          </FlexBox>
        </ModalBody>

        <FlexBox justifyContent="flex-end" gap="300" padding="500">
          <Button variant="tertiary" onClick={handleClose}>
            Cancel
          </Button>

          <FacebookLoginButton
            scope="pages_show_list,pages_manage_metadata,pages_messaging,pages_read_engagement,business_management"
            onSuccess={handleFacebookAuthenticated}
            platform="facebook"
          >
            Continue
          </FacebookLoginButton>
        </FlexBox>
      </Modal>

      <FacebookConnectModal />
    </>
  );
};
